import * as React from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface GlassSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface GlassSelectProps {
  value: string | null | undefined;
  onChange: (value: string) => void;
  options: GlassSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  'aria-label'?: string;
  /**
   * Liga/desliga o input de busca por digitacao dentro do popover.
   * Default (undefined): automatico, ativa quando options.length >= SEARCH_THRESHOLD.
   */
  searchable?: boolean;
}

// A partir de quantas opcoes o input de busca aparece automaticamente.
const SEARCH_THRESHOLD = 8;

function normalize(value: string): string {
  return value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

function filterOptions(list: GlassSelectOption[], term: string): GlassSelectOption[] {
  const trimmed = term.trim();
  if (!trimmed) return list;
  const needle = normalize(trimmed);
  return list.filter((opt) => normalize(opt.label).includes(needle));
}

export function GlassSelect({
  value,
  onChange,
  options,
  placeholder = 'Selecione',
  disabled = false,
  className,
  id,
  name,
  'aria-label': ariaLabel,
  searchable,
}: GlassSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [highlight, setHighlight] = React.useState<number>(-1);
  const [searchTerm, setSearchTerm] = React.useState('');
  const wrapRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const listRef = React.useRef<HTMLUListElement | null>(null);
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const listboxId = React.useId();

  const isSearchable = searchable ?? options.length >= SEARCH_THRESHOLD;

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  // Lista efetivamente renderizada/navegavel: filtrada quando a busca esta
  // ativa e ha texto digitado; caso contrario e a propria `options`. TODA
  // navegacao (highlight, commit, setas, Home/End, scroll) opera sobre
  // `visible` daqui pra baixo, nunca sobre `options` diretamente - evita
  // dessincronizar indice exibido x opcao real quando o filtro esta ativo.
  const visible = React.useMemo(
    () => (isSearchable ? filterOptions(options, searchTerm) : options),
    [isSearchable, options, searchTerm],
  );

  function openMenu() {
    if (disabled) return;
    setHighlight(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  }

  function closeMenu(focusTrigger = false) {
    setOpen(false);
    setHighlight(-1);
    setSearchTerm('');
    if (focusTrigger) triggerRef.current?.focus();
  }

  function commit(idx: number) {
    const opt = visible[idx];
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    closeMenu(true);
  }

  function onSearchChange(next: string) {
    setSearchTerm(next);
    // Reseta o highlight pro 1o item habilitado da lista JA filtrada com o
    // texto novo (nao dá pra confiar no `visible` do render atual, que ainda
    // reflete o searchTerm anterior).
    const filtered = filterOptions(options, next);
    setHighlight(filtered.findIndex((o) => !o.disabled));
  }

  React.useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) {
        closeMenu(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeMenu(true);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open || highlight < 0 || !listRef.current) return;
    const node = listRef.current.children[highlight] as HTMLElement | undefined;
    node?.scrollIntoView({ block: 'nearest' });
  }, [open, highlight]);

  // Foco automatico no input de busca ao abrir (padrao combobox).
  React.useEffect(() => {
    if (open && isSearchable) {
      searchInputRef.current?.focus();
    }
  }, [open, isSearchable]);

  function nextEnabled(start: number, dir: 1 | -1): number {
    if (visible.length === 0) return -1;
    let i = start;
    for (let step = 0; step < visible.length; step++) {
      i = (i + dir + visible.length) % visible.length;
      if (!visible[i]?.disabled) return i;
    }
    return -1;
  }

  function onTriggerKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlight((cur) => nextEnabled(cur < 0 ? -1 : cur, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlight((cur) => nextEnabled(cur < 0 ? 0 : cur, -1));
        break;
      case 'Home':
        e.preventDefault();
        setHighlight(nextEnabled(-1, 1));
        break;
      case 'End':
        e.preventDefault();
        setHighlight(nextEnabled(0, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlight >= 0) commit(highlight);
        break;
      case 'Tab':
        closeMenu(false);
        break;
    }
  }

  // Teclado do input de busca: o foco muda pra ca (via effect acima) assim
  // que o popover abre em modo searchable. Home/End ficam de fora de
  // proposito, pra nao brigar com o cursor de texto nativo do input.
  function onSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlight((cur) => nextEnabled(cur < 0 ? -1 : cur, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlight((cur) => nextEnabled(cur < 0 ? 0 : cur, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlight >= 0) commit(highlight);
        break;
      case 'Tab':
        closeMenu(false);
        break;
    }
  }

  const activeDescendantId =
    open && highlight >= 0 && highlight < visible.length
      ? `${listboxId}-option-${highlight}`
      : undefined;

  return (
    <div ref={wrapRef} className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        name={name}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={activeDescendantId}
        role="combobox"
        onClick={() => (open ? closeMenu(false) : openMenu())}
        onKeyDown={onTriggerKeyDown}
        className={cn(
          'flex h-9 w-full items-center justify-between rounded-xl border bg-[var(--surface)] px-2.5 text-sm transition',
          'border-[var(--border)] text-[var(--fg)]',
          'focus:outline-none focus:ring-2 focus:ring-[var(--accent)]',
          'disabled:cursor-not-allowed disabled:opacity-50',
          open && 'ring-2 ring-[var(--accent)]',
        )}
      >
        <span className={cn('truncate', !selected && 'text-[var(--fg-muted)]')}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown
          className={cn(
            'ml-2 h-3.5 w-3.5 shrink-0 text-[var(--fg-muted)] transition',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div
          className={cn(
            'absolute left-0 right-0 z-50 mt-1 overflow-hidden rounded-xl border shadow-lg',
            // Usa --popover-bg e --popover-border quando definidos (tema glass),
            // caindo em --card-bg/--card-border como fallback (light/dark).
            'border-[var(--popover-border,var(--card-border))]',
            'backdrop-blur-[var(--blur,0px)]',
          )}
          style={{
            background: 'var(--popover-bg, var(--card-bg))',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          {isSearchable && (
            <div className="border-b border-[var(--popover-border,var(--card-border))] p-1.5">
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder="Buscar..."
                aria-controls={listboxId}
                aria-activedescendant={activeDescendantId}
                className={cn(
                  'flex h-8 w-full rounded-lg border bg-[var(--surface)] px-2.5 text-sm',
                  'border-[var(--border)] text-[var(--fg)]',
                  'placeholder:text-[var(--fg-muted)]',
                  'focus:outline-none focus:ring-2 focus:ring-[var(--accent)]',
                )}
              />
            </div>
          )}
          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            tabIndex={-1}
            className="max-h-64 overflow-y-auto py-1"
          >
            {visible.length === 0 ? (
              <li className="px-3 py-2 text-xs text-[var(--fg-muted)]">
                Nenhuma opção.
              </li>
            ) : (
              visible.map((opt, idx) => {
                const isSelected = opt.value === value;
                const isHighlighted = idx === highlight;
                return (
                  <li
                    key={opt.value}
                    id={`${listboxId}-option-${idx}`}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={opt.disabled || undefined}
                    onMouseEnter={() => setHighlight(idx)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      commit(idx);
                    }}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition',
                      opt.disabled && 'cursor-not-allowed opacity-50',
                      !opt.disabled &&
                        isHighlighted &&
                        'bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]',
                      isSelected ? 'text-[var(--accent)]' : 'text-[var(--fg)]',
                    )}
                  >
                    <span className="flex-1 truncate">{opt.label}</span>
                    {isSelected && (
                      <Check className="h-3.5 w-3.5 shrink-0 text-[var(--accent)]" />
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
